import { IPriceOverride } from "../model/IPrice";

export interface DataLayerType {
  event: DataLayerEvent;
  form: {
    name: DataLayerFormName;
    stage: DataLayerFormStage;
    details?: DataLayerDetailsType;
    field?: DataLayerFormFieldType;
  };
  user?: DataLayerUserType;
}

export type VProductType = {
  series?: string;
  index?: string;
  name?: string;
  id?: string;
  nameplate?: string;
  bodystyle?: string;
  engine?: string;
  fuel_type?: string;
  color?: string;
  color_code?: string;
  model_year?: number;
  price?: number;
  est_delivery_time?: string;
  total_weight?: string;
  max_speed?: string;
  transmission?: string;
  max_output?: number | string;
  availability?: boolean;
};

export type CheckoutStepType = {
  step_number?: string;
  step_name?: string;
};

export type PurchaseOptionType = {
  payment_type?: string;
  monthly_payment?: string;
  interest_rate?: string;
  down_payment?: string;
  monthly_mileage?: string;
  reservation_fee?: string;
  price?: string;
  finance_product?: string;
  down_payment_trade_in_price?: string;
  final_deferred_payment?: string;
  installment_fee?: string;
  total_to_pay?: string;
  payment_period?: string | string;
  vehicle_cash_price?: string;
  terms?: string;
};

export type TradeInType = {
  brand?: string;
  body_type?: string;
  fuel_type?: string;
  model?: string;
  mileage?: string;
  date_of_registration?: string;
  registration_number?: string;
};

export type RetailerType = {
  name?: string;
  location?: string;
  city_name?: string;
  zip_code?: string;
  mobile_number?: string;
  email_address?: string;
};

export type BookingDetailsType = {
  date?: string;
  time?: string;
};

export type FormContextTradeInType = {
  agentType?: string;
  colour?: string;
  handleOverDate?: string;
  make?: string;
  makeAutocomplete?: string;
  markdownText?: string;
  mileage?: string;
  model?: string;
  ownerId?: string;
  ownerIdTypeAutocomplete?: string;
  registrationNumber?: string;
  tradeInCheckbox?: string;
  tradeInComment?: string;
  tradeInCommentAutoresize?: string;
  variant?: string;
  vehicleNumber?: string;
  year?: string;
  brand?: string;
};

export type EcommerceItemType = {
  item_id?: string;
  item_name?: string;
  affiliation?: string;
  item_brand?: string;
  item_category?: string;
  price?: string | number;
  index?: string;
  item_variant?: string;
  quantity?: string | number;
  value?: string;
  payment_type?: string;
};

export type EcommerceType = {
  transaction_id?: string;
  currency?: string;
  value?: string;
  tax?: string;
  shipping?: string;
  payment_type?: string;
  items?: EcommerceItemType[];
};

export type PageType = {
  page_type?: string;
  language?: string;
  currency_code?: string;
  brand?: string;
  consent_status?: string;
  category?: string;
  application?: string;
  market?: string;
};

export type UserType = {
  logged_in?: boolean | string;
  Id?: string | undefined;
};

export declare type OrderType = {
  date?: string;
  number?: string;
  vin_number?: string;
  status?: string;
};

export type EventDataType = {
  event_action?: string;
  event_category?: string;
  event_label?: string;
  event_non_int?: string;
  click_url?: string;
  click_text?: string;
  click_name?: string;
  event_pagecategory?: string;
  event?: DxpDataLayerEvent | DxpDataLayerEventNames;
};

export interface DxpDataLayerType1 {
  eventName?: DxpDataLayerEventNames;
  pageName?: string;
  pageTitle?: string;
  pageLocation?: string;
  referralPage?: string;
  pageDescription?: string;
  model?: string;
  motor?: string;
  version?: string;
  color?: string;
  interiorColor?: string;
  accessories?: string;
  dealerPreference?: string;
  exteriorColor?: string;
  keyspecs?: string;
  financingType?: string;
  repaymentChoice?: string;
  transmission?: string;
  bodyType?: string;
  engineType?: string;
  seatCapacity?: number | null;
  options?: string;
  servicePlan?: string | null;
  paymentMethod?: string;
  creditType?: string;
  vehiclePrice?: number | null;
  accessoriesPrice?: number | null;
  totalPrice?: number | null;
  downPayment?: number | null;
  downPaymentPercentage?: number | null;
  paymentPeriod?: number | null;
  financeAmount?: number | null;
  residualValue?: number | null;
  flatRate?: number | null;
  interestRate?: number | null;
  comparisonRate?: number | null;
  monthlyPayment?: number | null;
  DXPProduct?: string;
  nextSteps?: string[];
  stepName?: string;
  vproduct?: VProductType;
  checkoutStep?: CheckoutStepType;
  purchaseOption?: PurchaseOptionType;
  tradein?: TradeInType;
  retailer?: RetailerType;
  bookingDetails?: BookingDetailsType;
  ecommerce?: EcommerceType | null;
  user?: UserType;
  order?: OrderType;
  page?: PageType;
}

export type DxpDataLayerType = DxpDataLayerType1 & Partial<EventDataType>;

export enum FinanceResultType {
  cash = "cash",
  error = "error",
  finance = "finance",
  na = "na",
  not_applicable = "not_applicable",
}

enum FinanceFormType {
  cash = "cash",
  finance = "finance",
}

export enum CarBrands {
  BMW = "bavarian motor works",
  BRV = "bravo auto",
  BYD = "biyadi",
  CHA = "changan",
  CHV = "chevrolet",
  CTR = "citroen",
  DFS = "dongfeng sokon",
  DSE = "distinctive series",
  GLY = "geely",
  GRW = "great wall hongkong",
  GWM = "great wall indonesia",
  HIN = "hino",
  JAC = "jianghuai automobile co ltd",
  JAG = "jaguar",
  LEX = "lexus",
  LRE = "land rover",
  MER = "mercedes",
  MINI = "mini",
  MOT = "motorrad",
  MZD = "mazda",
  REN = "renault",
  SUB = "subaro",
  SZK = "suzuki",
  VLV = "volvo",
}

export interface DataLayerDetailsType {
  configurator?: {
    financeOption?: FinanceResultType;
    modelList?: CatalogModalListType[] | CatalogModalListTypeRest[];
    dealId?: string | null;
    specPacks?: SpeckPackListType[];
    selectedSpec?: SelectedSpecPackListType[];
  };
  finance?: {
    amountPayable?: number | null;
    cashDeposit?: number | null;
    comparisonRate?: number | null;
    financeType?: string | null;
    homeOwner?: boolean | string | null;
    interestRate?: number | null;
    loanFees?: number | null;
    loanTerm?: number | null;
    paymentMethod: FinanceResultType | FinanceFormType;
    purchaseAmount?: number | null;
    age?: string | null;
    paymentFrequency?: string | null;
    annualKmAllowance?: number | null;
    guaranteedFutureValue?: number | null;
    paymentAmount?: number | null;
  };
}

export enum DxpPageType {
  REAL = "real page",
  VIRTUAL = "virtual page",
}

export const enum DxpDataLayerEvent {
  CONFIGURATOR = "Configurator",
  CHECKOUT = "Checkout",
}

export const enum DxpDataLayerEventNames {
  CONFIGURATOR_ENTRY = "CNF_EntryPoint",
  CONFIGURATOR_CUSTOMIZE = "CNF_CustomizeCar",
  CONFIGURATOR_LEAD = "CNF_Lead",
  CONFIGURATOR_MODEL = "CNF_ModelClicked",
  CONFIGURATOR_MOTOR = "CNF_MotorClicked",
  CONFIGURATOR_VERSIONS = "CNF_VersionsClicked",
  CONFIGURATOR_EXTERIOR = "CNF_ExteriorColorsClicked",
  CONFIGURATOR_INTERIOR = "CNF_InteriorColorsClicked",
  CONFIGURATOR_ACCESSORIES = "CNF_AccessoriesClicked",
  CONFIGURATOR_CALCULATE_BUTTON = "CNF_CalculateButtonClicked",
  CONFIGURATOR_TEST_DRIVE = "CNF_TestDriveClicked",
  CHECKOUT_LEAD = "CNF_Lead",
  CONFIGURATOR_QUOTE = "CNF_QuoteClicked",
  CNF_DEALERS_CLICKED = "CNF_DealersClicked",
  PAGE_VIEW = "page_view",
  VEHICLE_SELECTION_DETAILS = "checkout_your_vehicle_selection_details",
  BEGIN_CHECKOUT = "begin_checkout",
  BACK_CTA_CLICK = "checkout_reservation_details_back_cta_click",
  DETAILS_CTA_CLICK = "checkout_edit_your_reservation_details_cta_click",
  CONFIGURATION_CTA_CLICK = "checkout_open_your_configuration_cta_click",
  FINANCE_CTA_CLICK = "checkout_purchase_option_finance_cta_click",
  PURCHASE_OPTION_NEXT_CTA_CLICK = "checkout_purchase_option_next_cta_click",
  PRODUCTS_CTA_CLICK = "checkout_select_additional_products_cta_click",
  ADDITIONAL_PRODUCTS_CTA_CLICK = "checkout_additional_products_next_cta_click",
  RETAILER_DETAILS_CTA_CLICK = "checkout_select_retailer_details_cta_click",
  RETAILER_CONFIRM_DETAILS_CTA_CLICK = "checkout_confirm_retailer_details_cta_click",
  ADD_SHIPPING_INFO = "add_shipping_info",
  TRADEIN_CTA_CLICK = "checkout_add_vehicle_trade_in_cta_click",
  TRADEIN_NO_CTA_CLICK = "checkout_trade_in_no_cta_click",
  TRADEIN_BRAND_CTA_CLICK = "checkout_trade_in_brand_cta_click",
  TRADEIN_MODEL_CTA_CLICK = "checkout_trade_in_model_cta_click",
  TRADEIN_MILEAGE_CTA_CLICK = "checkout_trade_in_mileage_cta_click",
  TRADEIN_REGISTRATION_NUMBER_CTA_CLICK = "checkout_trade_in_add_registration_number_cta_click",
  TRADEIN_DATE_OF_REGISTRATION_CTA_CLICK = "checkout_trade_in_date_of_registration_cta_click",
  TRADEIN_DETAILS_CTA_CLICK = "checkout_submit_trade_in_details_cta_click",
  TNC_PRIVACY_CTA_CLICK = "checkout_checkbox_tnc_privacy_cta_click",
  CONFIRM_RESERVATION_CTA_CLICK = "checkout_reservation_details_confirm_reservation_cta_click",
  ADD_PAYMENT_INFO = "add_payment_info",
  ORDER_CONFIRMED = "checkout_vehicle_order_confirmed",
  PURCHASE = "purchase",
  CONFIGURATOR_URL_CTA_CLICK = "checkout_copy_configurator_url_cta_click",
}

export const enum DataLayerDXPProducts {
  FINANCE_CALCULATOR = "Finance Calculator",
}

export const enum DataLayerEvent {
  FORM_NAVIGATE = "_formNavigate",
  FORM_INTERACT = "_formInteract",
}

export const enum DataLayerFormName {
  CONFIGURATOR = "configurator",
  CONFIGURATOR_STOCK_RESERVATION = "configurator_stock_reservation",
  REPAYMENT_CALCULATOR = "repayment calculator",
}

export const enum DataLayerFormStage {
  CATALOG = "catalog",
  CONTINUE_CHECKOUT = "continue",
  CONFIGURE_CAR = "configure your car",
  VARIANT_CONFIGURATOR = "variant",
  PAYMENT_DISPLAY = "payment method displayed",
  CHECKOUT = "checkout",
  START = "start",
  CHECKOUT_CONFIRMATION = "enquire submitted",
  DEALER = "enquire select retailer",
  SELECT_DEALER = "select dealer",
  ENQUIRY_FORM = "enquire customer information",
  CUSTOMER_INFORMATION = "customer information",
  PAYMENT_METHOD = "payment_method",
  EMAIL_MY_CONFIGURATION_SUBMITTED = "email my configuration submitted",
  FINALISE_TRANSACTION = "finalise transaction",
}
export const enum DesignType {
  INTERIOR = "subaru_interior",
  SUBARU_EXTERIOR = "subaru_colour",
}

export const enum FormFieldName {
  COLOR = "colour",
  SPECKPACK = "specPack",
  LOANTERM = "loan term",
}

interface DataLayerFormFieldType {
  name: FormFieldName | string;
  value?: string | boolean | number | null;
}

interface DataLayerUserType {
  postCode?: string | null;
  emailHashed?: string | null;
  phoneHashed?: string | null;
  inchid?: DataLayerInchIdType | null;
  preferredDealership?: string;
}

interface DataLayerInchIdType {
  acidPrid?: string | null;
  prid?: string | null;
  loggedIn?: boolean | null;
}

interface CatalogModalListType {
  model: string;
  driveAwayPricing: IPriceOverride;
  manufacturerListPricing: IPriceOverride;
}
[];

export interface CatalogModalListTypeRest {
  model: string;
  driveAwayPricing: number;
  manufacturerListPricing: number;
}

export interface SelectedSpecPackListType {
  nameplate?: {
    code: string | null;
    name: string | null;
    modelYear: string | null;
    brand: string | null;
    description: string | null;
  };
  specPack?: {
    code: string | null;
    name: string | null;
  };
  engine?: {
    description?: string | null;
    fuelType?: string | null;
    fuelLabel?: string | null;
    powerTrain?: string | null;
    capacity?: string | null;
    maximumPower?: string | null;
    enginePower?: string | null;
    maximumTorque?: string | null;
    transmission?: string | null;
    transmissionType?: string | null;
  };
  exterior?: InteriorExteriorType;
  interior?: InteriorExteriorType;
  bodyType?: {
    bodyStyleType: string | null;
    description?: string | null;
    code?: string | null;
    price?: {
      totalPrice: number | null;
      currency: string | null;
    };
  };
  standardFeatures?: FeaturesType[];
  addOnFeatures?: FeaturesType[];
  prices?: PriceType[];
  trim?: {
    attributes: {
      label: string | null;
      marketingprefix: string | null;
    };
  };
}

export interface SpeckPackListType {
  nameplate: {
    code?: string | null;
    name?: string | null;
    modelYear: string | null;
    brand?: string | null;
    description?: string | null;
  };
  specPack?: {
    code?: string | null;
    name?: string | null;
  };
  engine?: {
    description?: string | null;
    fuelType?: string | null;
    fuelLabel?: string | null;
    powerTrain?: string | null;
    capacity?: string | null;
    maximumPower?: string | null;
    enginePower?: string | null;
    maximumTorque?: string | null;
    transmission?: string | null;
    transmissionType?: string | null;
  };
  exterior?: InteriorExteriorType;
  interior?: InteriorExteriorType;
  bodyType?: {
    bodyStyleType?: string | null;
    description?: string | null;
    code?: string | null;
    price?: {
      totalPrice: number | null;
      currency: string | null;
    };
  };
  standardFeatures?: FeaturesType[];
  addOnFeatures?: FeaturesType[];
  prices: PriceType[];
}

export interface InteriorExteriorType {
  colour: string | null;
  code: string | null;
  price?: {
    totalPrice: number | null;
    currency: string | null;
  };
}

export interface FeaturesType {
  id?: number | null;
  name?: string | null;
  code?: string | null;
  category?: string | null;
  price?: {
    totalPrice: number | null;
    currency: string | null;
  };
}

export interface PriceType {
  carPrice?: {
    originalPrice?: IPriceOverride | number;
    totalDiscount?: number | null;
    finalPrice?: IPriceOverride | number;
    offers?: OfferType[];
  };
  totalFeatures?: number | null;
  totalConfiguration?: number | null;
  totalPrice?: number | null;
  currency: string | null;
  taxes?: TaxType[];
}

export interface OfferType {
  offer?: string | null;
  amount?: number | null;
}

interface TaxType {
  amount: number | null;
  type: string | null;
}

export interface FinanceProduct {
  key: string;
  value: {
    json: {
      value: string;
      tooltip: string;
      disclaimerAnchor: string;
      disabled?: boolean;
      message?: string | null;
    };
  };
}
export interface FinanceBreakdownItem {
  title: string;
  value: string;
  style: {
    fontWeight: string;
  };
  shouldBeFormatted: boolean;
  isNegative: boolean;
  tooltips?: {
    description: string;
    translationPlaceholders: string[];
  }[];
  key?: string;
}

export enum SimilarStockSelect {
  NEAR = "near",
  EXACT = "exact",
}
